import { format, parseISO } from 'date-fns'
import { DateRange, RoiStats, Stat } from '../types'
import GraphTooltip from './GraphTooltip/GraphTooltip'
import {
  monthlyRangeFormat,
  quarterlyRangeFormat,
  weekRangeFormat,
} from '../constants'

const ORGANIC_SEARCH = 'Organic Search'
const NON_BRANDED = 'non_branded'

export const transformGraphData = ({
  stats,
  period,
}: {
  stats: RoiStats
  period: DateRange
}): {
  traffic: Stat
  visitors: Stat
  rankingKeywords: Stat
} => {
  const { aggregate, insights, timeseries } = stats
  const {
    visitors: visitorsAggregate,
    traffic: trafficAggregate,
    ranking_keywords: rankingKeywordsAggregate,
  } = aggregate
  const {
    traffic: trafficTimeseries,
    visitors: visitorsTimeseries,
    ranking_keywords: rankingKeywordsTimeseries,
  } = timeseries

  const trafficProgress = trafficAggregate.breakdown.find(
    _breakdown => _breakdown.session_default_channel_group === ORGANIC_SEARCH
  )
  const visitorsProgress = visitorsAggregate.breakdown.find(
    _breakdown => _breakdown.session_default_channel_group === ORGANIC_SEARCH
  )
  const rankingKeywordsProgress = rankingKeywordsAggregate.breakdown.find(
    _breakdown => _breakdown.query_type === NON_BRANDED
  )

  const trafficInsight = insights?.traffic?.[Object.keys(insights?.traffic)[0]]
  const visitorsInsight =
    insights?.visitors?.[Object.keys(insights?.visitors)[0]]
  const rankingKeywordsInsight =
    insights?.ranking_keywords?.[Object.keys(insights?.ranking_keywords)[0]]

  const dateFn: Record<DateRange, (dateStr: string) => string> = {
    weekly: dateStr => format(parseISO(dateStr), weekRangeFormat),
    monthly: dateStr => format(parseISO(dateStr), monthlyRangeFormat),
    quarterly: dateStr => format(parseISO(dateStr), quarterlyRangeFormat),
    yearly: dateStr => format(parseISO(dateStr), 'YYY'),
  }

  const traffic: Stat = {
    id: 1,
    title: 'Total Traffic',
    value: trafficAggregate.value,
    change: trafficAggregate.growth_percentage,
    yAxisLabel: 'Traffic Volume',
    progressTitle: 'Organic Search Traffic',
    progressValue: trafficProgress?.value,
    progressChange: trafficProgress?.growth_percentage,
    progressPercent: trafficProgress?.percentage,
    tooltip: (
      <GraphTooltip
        title1="Total Traffic"
        description1="Total visitors to your website, including organic and non-organic
            sources."
        title2="Organic Search Traffic"
        description2="Visitors from unpaid search results on search engines."
      />
    ),
    graphData: [
      {
        label: 'Total Traffic',
        data: trafficTimeseries
          .filter(item => item.session_default_channel_group === 'All')
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
      {
        label: 'Organic Search Traffic',
        data: trafficTimeseries
          .filter(item => item.session_default_channel_group === ORGANIC_SEARCH)
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
    ],
    insight: {
      description: trafficInsight?.display_text ?? '',
      action: trafficInsight?.actionable ?? 'content_refresh',
      cta: trafficInsight?.actionable_text ?? '',
      impact: trafficInsight?.impact ?? 'low',
    },
  }
  const visitors: Stat = {
    id: 2,
    title: 'Unique Visitors',
    value: visitorsAggregate.value,
    change: visitorsAggregate.growth_percentage,
    yAxisLabel: 'No. of Visitors',
    progressTitle: 'Organic Search Visitors',
    progressValue: visitorsProgress?.value,
    progressChange: visitorsProgress?.growth_percentage,
    progressPercent: visitorsProgress?.percentage,
    tooltip: (
      <GraphTooltip
        title1="Unique Visitors"
        description1="Count of distinct individuals who visited your website."
        title2="Organic Search Visitors"
        description2="Unique visitors from unpaid search results."
      />
    ),
    graphData: [
      {
        label: 'Unique Visitors',
        data: visitorsTimeseries
          .filter(item => item.session_default_channel_group === 'All')
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
      {
        label: 'Organic Search Visitors',
        data: visitorsTimeseries
          .filter(item => item.session_default_channel_group === ORGANIC_SEARCH)
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
    ],
    insight: {
      description: visitorsInsight?.display_text ?? '',
      action: visitorsInsight?.actionable ?? 'content_refresh',
      cta: visitorsInsight?.actionable_text ?? '',
      impact: visitorsInsight?.impact ?? 'low',
    },
  }
  const rankingKeywords: Stat = {
    id: 3,
    title: 'Total Ranking Keywords',
    value: rankingKeywordsAggregate.value,
    change: rankingKeywordsAggregate.growth_percentage,
    yAxisLabel: 'No. of Keywords',
    progressTitle: 'Non-Branded Keywords',
    progressValue: rankingKeywordsProgress?.value,
    progressChange: rankingKeywordsProgress?.growth_percentage,
    progressPercent: rankingKeywordsProgress?.percentage,
    tooltip: (
      <GraphTooltip
        title1="Total Ranking Keywords"
        description1="Overall keywords where your website appears in search results."
        title2="Non-Branded Keywords"
        description2="Keywords not directly related to your brand name or specific
            offerings."
      />
    ),
    graphData: [
      {
        label: 'Total Ranking Keywords',
        data: rankingKeywordsTimeseries
          .filter(item => item.query_type === 'All')
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
      {
        label: 'Non-Branded Keywords',
        data: rankingKeywordsTimeseries
          .filter(item => item.query_type === NON_BRANDED)
          .map(item => ({
            date: dateFn[period](item.date_range),
            value: item.value,
          })),
      },
    ],
    insight: {
      description: rankingKeywordsInsight?.display_text ?? '',
      action: rankingKeywordsInsight?.actionable ?? 'content_refresh',
      cta: rankingKeywordsInsight?.actionable_text ?? '',
      impact: rankingKeywordsInsight?.impact ?? 'low',
    },
  }

  return { traffic, visitors, rankingKeywords }
}
